import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { frontendAnalyticsTrack } from 'analytics/utils';
import GlobalLoader from 'components/GlobalLoader';
import ScrollToTop from 'components/ScrollToTop';
import Basics from 'containers/Basics';
import { InterviewRouter } from 'containers/Interview/InterviewRouter';
import Start from 'containers/Start';

import {
  APPLICATION_FULL_PATH,
  APPLICATION_BENEFICIARIES_FULL_PATH,
  APPLICATION_IN_PROGRESS_FULL_PATH,
  BASICS_BASE_PATH,
  HEALTH_PROFILE_BASE_PATH,
  PERMANENT_POLICY_APPLICATION_FULL_PATH,
  APPLIED_POLICY_REDIRECT_REASON,
  REDIRECT_EVENT,
  START_FULL_PATH,
  ESTIMATES_FULL_PATH,
  PARTNERS_FULL_PATH,
  INFO_FULL_PATH,
  OFFBOARD_CLIENT_LED_REFERRAL_FULL_PATH,
  MOTIVATIONS_FULL_PATH,
  MY_QUOTES_FULL_PATH,
  APPLY_FULL_PATH,
  QUOTES_BASE_PATH,
  COMMON_APP_FULL_PATH,
  AI_ASSISTANT_FULL_PATH,
  LIFESTYLE_FULL_PATH,
  BASIC_WHOLE_LIFE_FULL_PATH,
  BEST_FOR_YOU_FULL_PATH,
  INTERVIEW_BASE_PATH,
  POLICY_REPLACEMENT_FULL_PATH,
  POLICY_OWNER_FULL_PATH,
  PRUDENTIAL_IDENTITY_VERIFICATION_FULL_PATH,
  RESIDENTIAL_ADDRESS_FULL_PATH,
  PROGRESSIVE_FULL_PATH,
  PRUDENTIAL_INTERVIEW_BASE_PATH,
  PRUDENTIAL_CONSENT_FULL_PATH,
  PRUDENTIAL_UNDERWRITING_APPROVED_FULL_PATH,
  REVIEW_INFORMATION_FULL_PATH,
} from './constants';
import HealthRoutes from './healthRoutes';
import {
  Partners,
  Estimates,
  PermanentPolicyApplication,
  ApplicationInProgress,
  Application,
  ApplicationBeneficiaries,
  ClientReferralSafetyNet,
  Motivations,
  MyQuotes,
  Apply,
  Quotes,
  CommonApp,
  BasicWholeLifeApplication,
  BestForYou,
  PolicyReplacement,
  PolicyOwner,
  IdentityVerification,
  ResidentialAddress,
  Progressive,
  AiAssistant,
  PrudentialInterview,
  PrudentialConsent,
  PrudentialUnderwritingApproved,
  ReviewInformation,
} from './lazyLoadableComponents';
import { LifeStyleRoutes } from './lifeStyleRoutes';
import LoadingComponent from './LoadingComponent';
import { Props, RedirectProps } from './types';

export const Redirect = ({ to, from, reason }: RedirectProps) => {
  frontendAnalyticsTrack(REDIRECT_EVENT, {
    from,
    to: typeof to === 'string' ? to : to.pathname,
    reason,
  });

  return <Navigate to={to} replace />;
};

const AppRouter: React.FC<Props> = ({
  redirectToEstimates,
  hasEmail,
  isLoading,
  isValidCustomer,
}) => {
  const { pathname } = useLocation();

  return (
    <ScrollToTop>
      {!isLoading &&
        !hasEmail &&
        !isValidCustomer &&
        ![
          START_FULL_PATH,
          INFO_FULL_PATH,
          MY_QUOTES_FULL_PATH,
          PROGRESSIVE_FULL_PATH,
          AI_ASSISTANT_FULL_PATH,
        ].includes(pathname) && (
          <Redirect from={pathname} to={START_FULL_PATH} reason="life_started_flow not present" />
        )}
      {redirectToEstimates &&
        !(
          pathname === ESTIMATES_FULL_PATH ||
          pathname === APPLICATION_IN_PROGRESS_FULL_PATH ||
          pathname === START_FULL_PATH ||
          pathname.match(MY_QUOTES_FULL_PATH) ||
          pathname.match(APPLY_FULL_PATH)
        ) && (
          <Redirect
            from={pathname}
            to={ESTIMATES_FULL_PATH}
            reason={APPLIED_POLICY_REDIRECT_REASON}
          />
        )}
      <React.Suspense fallback={<LoadingComponent />}>
        <Routes>
          <Route path={START_FULL_PATH} element={<Start />} />
          <Route path={`${BASICS_BASE_PATH}/*`} element={<Basics />} />
          <Route path={`${HEALTH_PROFILE_BASE_PATH}/*`} element={<HealthRoutes />} />
          <Route path={MOTIVATIONS_FULL_PATH} element={<Motivations />} />
          <Route path={PARTNERS_FULL_PATH} element={<Partners />} />
          <Route path={PROGRESSIVE_FULL_PATH} element={<Progressive />} />
          <Route path={AI_ASSISTANT_FULL_PATH} element={<AiAssistant />} />
          <Route
            path={ESTIMATES_FULL_PATH}
            element={
              // Estimates has a specific fallback of <GlobalLoader /> to avoid a
              // flash when using the <LoadingComponent />
              <React.Suspense fallback={<GlobalLoader />}>
                <Estimates />
              </React.Suspense>
            }
          />
          <Route
            path={PERMANENT_POLICY_APPLICATION_FULL_PATH}
            element={<PermanentPolicyApplication />}
          />
          <Route path={BASIC_WHOLE_LIFE_FULL_PATH} element={<BasicWholeLifeApplication />} />
          <Route path={APPLICATION_IN_PROGRESS_FULL_PATH} element={<ApplicationInProgress />} />
          <Route
            path={APPLICATION_BENEFICIARIES_FULL_PATH}
            element={
              <React.Suspense fallback={<GlobalLoader />}>
                <ApplicationBeneficiaries />
              </React.Suspense>
            }
          />
          <Route
            path={APPLICATION_FULL_PATH}
            element={
              // Application has a specific fallback of <GlobalLoader /> to avoid a
              // flash when using the <LoadingComponent />
              <React.Suspense fallback={<GlobalLoader />}>
                <Application />
              </React.Suspense>
            }
          />
          <Route
            path={OFFBOARD_CLIENT_LED_REFERRAL_FULL_PATH}
            element={<ClientReferralSafetyNet />}
          />
          <Route
            path={`${BEST_FOR_YOU_FULL_PATH}/*`}
            element={
              // BestForYou has a specific fallback of <GlobalLoader /> to avoid a
              // flash when using the <LoadingComponent />
              <React.Suspense fallback={<GlobalLoader />}>
                <BestForYou />
              </React.Suspense>
            }
          />
          <Route
            path={`${QUOTES_BASE_PATH}/*`}
            element={
              // MyQuotes has a specific fallback of <GlobalLoader /> to avoid a
              // flash when using the <LoadingComponent />
              <React.Suspense fallback={<GlobalLoader />}>
                <Quotes />
              </React.Suspense>
            }
          />
          <Route
            path={`${MY_QUOTES_FULL_PATH}/*`}
            element={
              // MyQuotes has a specific fallback of <GlobalLoader /> to avoid a
              // flash when using the <LoadingComponent />
              <React.Suspense fallback={<GlobalLoader />}>
                <MyQuotes />
              </React.Suspense>
            }
          />
          <Route
            path={`${APPLY_FULL_PATH}/*`}
            element={
              <React.Suspense fallback={<GlobalLoader />}>
                <Apply />
              </React.Suspense>
            }
          />
          <Route
            path={`${COMMON_APP_FULL_PATH}/*`}
            element={
              <React.Suspense fallback={<GlobalLoader />}>
                <CommonApp />
              </React.Suspense>
            }
          />
          <Route
            path={`${LIFESTYLE_FULL_PATH}/*`}
            element={
              <React.Suspense fallback={<GlobalLoader />}>
                <LifeStyleRoutes />
              </React.Suspense>
            }
          />
          <Route path={`${INTERVIEW_BASE_PATH}/*`} element={<InterviewRouter />} />
          <Route path={POLICY_REPLACEMENT_FULL_PATH} element={<PolicyReplacement />} />
          <Route path={POLICY_OWNER_FULL_PATH} element={<PolicyOwner />} />
          <Route
            path={PRUDENTIAL_IDENTITY_VERIFICATION_FULL_PATH}
            element={<IdentityVerification />}
          />
          <Route
            path={`${PRUDENTIAL_INTERVIEW_BASE_PATH}/*`}
            element={
              <React.Suspense fallback={<GlobalLoader />}>
                <PrudentialInterview />
              </React.Suspense>
            }
          />
          <Route path={RESIDENTIAL_ADDRESS_FULL_PATH} element={<ResidentialAddress />} />
          <Route path={PRUDENTIAL_CONSENT_FULL_PATH} element={<PrudentialConsent />} />
          <Route
            path={PRUDENTIAL_UNDERWRITING_APPROVED_FULL_PATH}
            element={<PrudentialUnderwritingApproved />}
          />
          <Route path={REVIEW_INFORMATION_FULL_PATH} element={<ReviewInformation />} />
        </Routes>
      </React.Suspense>
    </ScrollToTop>
  );
};

export default AppRouter;
